// src/app/features/document/components/documents-issued/documents-issued.component.ts

import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { MatButtonModule } from '@angular/material/button';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { DocumentService } from '../../services/DocumentService/document.service';
import { NumberReservationService } from '../../services/NumberReservation/number-reservation.service'; 
import { CreateDocumentDialogComponent } from '../CreateDocumentDialogComponent/create-document-dialog.component'; 
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-documents-issued',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatRadioModule,
    MatButtonModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatDialogModule,
    MatSnackBarModule,
    CreateDocumentDialogComponent,
  ],
  templateUrl: './documents-issued.component.html',
})
export class DocumentsIssuedComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  displayedColumns: string[] = [
    'numeroDocumento',
    'titulo',
    'tipoDocumento',
    'situacao',
    'dataEmissao',
    'acoes'
  ];
  dataSource!: MatTableDataSource<any>;
  
  filter = {
    situacao: '',
    tipo: 'cpf',
    cpf: '',
    protocolo: '',
    tipoDocumento: '',
    usuarioEnvio: '',
    tipoArquivo: '',
    ano: '',
    numeroDocumento: ''
  };

  situacoes = ['Aprovado', 'Rejeitado', 'Recebido', 'Em análise', 'Não analisado', 'Vencido'];
  tiposDocumento = ['Comunicado', 'Documento', 'Relatório'];
  tiposArquivo = ['Imagem JPG', 'PDF', 'Docx'];

  constructor(
    private documentService: DocumentService,
    private numberReservationService: NumberReservationService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {
    this.dataSource = new MatTableDataSource();
  }

  ngOnInit(): void {
    this.consultarDocumentos();
  }

  ngAfterViewInit() {
    if (this.dataSource) {
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
    }
  }

  consultarDocumentos(): void {
    this.documentService.getDocuments(this.filter).subscribe({
      next: (documents) => {
        this.dataSource.data = documents;
        this.configurarDataSource();
      },
      error: (error) => {
        console.error('Erro ao consultar documentos:', error);
        this.snackBar.open('Erro ao carregar documentos', 'Fechar', { duration: 3000 });
      }
    });
  }

  private configurarDataSource(): void {
    if (this.dataSource) {
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
      this.dataSource.filterPredicate = (data: any, filter: string) => {
        const searchStr = filter.toLowerCase();
        return (
          data.numeroDocumento?.toLowerCase().includes(searchStr) ||
          data.titulo?.toLowerCase().includes(searchStr) ||
          data.tipoDocumento?.toLowerCase().includes(searchStr) ||
          data.situacao?.toLowerCase().includes(searchStr)
        );
      };
    }
  }

  openCreateDocumentDialog(): void {
    const dialogRef = this.dialog.open(CreateDocumentDialogComponent, {
      width: '600px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.createDocument(result);
      }
    });
  }

  async createDocument(document: any): Promise<void> {
    try {
      const documentNumber = await firstValueFrom(
        this.numberReservationService.reserveNumber('document')
      );

      const documentWithNumber = {
        ...document,
        numeroDocumento: documentNumber,
        dataEmissao: new Date().toISOString(),
        situacao: 'Não analisado'
      };

      this.documentService.createDocument(documentWithNumber).subscribe({
        next: (response) => {
          this.consultarDocumentos();
          this.snackBar.open('Documento criado com sucesso!', 'Fechar', { duration: 3000 });
        },
        error: (error) => {
          console.error('Erro ao criar documento:', error);
          this.snackBar.open('Erro ao criar documento', 'Fechar', { duration: 3000 });
        }
      });
    } catch (error) {
      console.error('Erro ao reservar número do documento:', error);
      this.snackBar.open('Erro ao gerar número do documento', 'Fechar', { duration: 3000 });
    }
  }

  updateDocument(id: number, document: any): void {
    this.documentService.updateDocument(id, document).subscribe({
      next: (response) => {
        this.consultarDocumentos();
        this.snackBar.open('Documento atualizado com sucesso!', 'Fechar', { duration: 3000 });
      },
      error: (error) => {
        console.error('Erro ao atualizar documento:', error);
        this.snackBar.open('Erro ao atualizar documento', 'Fechar', { duration: 3000 });
      }
    });
  }

  deleteDocument(id: number): void {
    if (confirm('Tem certeza que deseja excluir este documento?')) {
      this.documentService.deleteDocument(id).subscribe({
        next: (response) => {
          this.consultarDocumentos();
          this.snackBar.open('Documento excluído com sucesso!', 'Fechar', { duration: 3000 });
        },
        error: (error) => {
          console.error('Erro ao excluir documento:', error);
          this.snackBar.open('Erro ao excluir documento', 'Fechar', { duration: 3000 });
        }
      });
    }
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  limparFiltros(): void {
    this.filter = {
      situacao: '',
      tipo: 'cpf',
      cpf: '',
      protocolo: '',
      tipoDocumento: '',
      usuarioEnvio: '',
      tipoArquivo: '',
      ano: '',
      numeroDocumento: ''
    };
    
    if (this.dataSource) {
      this.dataSource.filter = '';
      
      if (this.dataSource.paginator) {
        this.dataSource.paginator.firstPage();
      }
    }
    
    this.consultarDocumentos();
  }

  visualizarDocumento(documento: any): void {
    // Implement document visualization logic
    console.log('Visualizando documento:', documento);
  }

  downloadDocumento(documento: any): void {
    // Implement document download logic
    console.log('Baixando documento:', documento);
  }

  getStatusClass(situacao: string): string {
    switch (situacao.toLowerCase()) {
      case 'aprovado':
        return 'status-aprovado';
      case 'rejeitado':
        return 'status-rejeitado';
      case 'em análise':
        return 'status-em-analise';
      case 'não analisado':
        return 'status-nao-analisado';
      case 'vencido':
        return 'status-vencido';
      default:
        return '';
    }
  }

  formatDate(date: string): string {
    if (!date) return '';
    return new Date(date).toLocaleDateString('pt-BR');
  }

  onSituacaoChange(): void {
    this.consultarDocumentos();
  }

  onTipoDocumentoChange(): void {
    this.consultarDocumentos();
  }

  onTipoArquivoChange(): void {
    this.consultarDocumentos();
  }

  isFilterActive(): boolean {
    return Object.values(this.filter).some(value => value !== '' && value !== 'cpf');
  }

  exportarDocumentos(): void {
    console.log('Exportando documentos...');
  }

  imprimirDocumento(documento: any): void {
    console.log('Imprimindo documento:', documento);
  }

  compartilharDocumento(documento: any): void {
    console.log('Compartilhando documento:', documento);
  }

  verificarPermissao(acao: string): boolean {
    return true; 
  }

  trackByFn(index: number, item: any): number {
    return item.id || index;
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }
}